// Purpose: shared Icon wrapper. Every icon in AST is a 24x24 viewBox SVG
// rendered at a consumer-specified size. stroke-width is 2 by default
// (deliberate — matches the industrial aesthetic); override via prop.
//
// Fill is always `none`; stroke inherits from currentColor so the icon
// re-colors through parent text color. Line caps + joins are round for
// the Lucide-like look.

import type { SVGProps, ReactNode } from 'react';
import { forwardRef } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** Pixel size. Width + height are set to this value. Default 16. */
  size?: number;
  /** SVG stroke-width. Default 2. */
  strokeWidth?: number;
  /** Accessible label; when omitted, icon is marked aria-hidden. */
  label?: string;
  children?: ReactNode;
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { size = 16, strokeWidth = 2, label, children, className, ...rest },
  ref
) {
  const ariaProps = label
    ? { role: 'img' as const, 'aria-label': label }
    : { 'aria-hidden': true as const };
  return (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...ariaProps}
      {...rest}
    >
      {children}
    </svg>
  );
});
